// Two HTML-string transforms applied after markdown-it renders:
//
// 1. wrapHeadingsForFold(html) — wraps each heading + its sibling
//    run (until the next heading at the same or higher level) in
//      <div class="heading-block" data-heading-id="..." data-heading-level="N">
//        <h*>...</h*>
//        <div class="heading-section">…siblings…</div>
//      </div>
//    so a runtime click handler can flip an `is-folded` class on
//    the wrapper.
//
// 2. wrapCodeBlocksForCopy(html) — wraps each <pre> in
//      <div class="code-block">
//        <div class="code-meta">
//          <span class="code-lang">{lang}</span>
//          <button class="code-copy-btn" type="button" aria-label="Copy code">
//            <span class="code-copy-label">Copy</span>
//          </button>
//        </div>
//        <pre …>…</pre>
//      </div>
//
// Both use jsdom-compatible DOMParser. In the browser, the global
// DOMParser is used. In jsdom-test runs, the same global is set up
// by the test environment.

const HEADING_RE = /^H([1-6])$/;

const FOLD_TOGGLE_HTML =
  '<button class="heading-fold-toggle" type="button" aria-expanded="true" aria-label="Collapse section">' +
    '<svg class="heading-fold-chev" viewBox="0 0 12 12" aria-hidden="true">' +
      '<path d="M3 4.5 L6 7.5 L9 4.5" stroke="currentColor" stroke-width="1.4" fill="none"/>' +
    '</svg>' +
  '</button>';

// Recursively wraps headings + their following siblings into nested
// .heading-block containers. A section under H1 contains the H2 that
// follows it (and that H2 gets its own heading-block inside the H1's
// heading-section), and so on. The fold-toggle button is injected
// inside the heading at this step so it survives every render's
// innerHTML rewrite — the SPA used to inject it from a useEffect after
// commit, which raced against async Shiki re-renders and disappeared
// when the second shiki-ready event committed a fresh innerHTML.
function wrapNodeListForFold(doc, sourceNodes, parentLevel) {
  const out = [];
  let i = 0;
  while (i < sourceNodes.length) {
    const node = sourceNodes[i];
    const tag = node.nodeType === 1 ? node.tagName : '';
    const m = HEADING_RE.exec(tag || '');
    if (!m) {
      out.push(node.cloneNode(true));
      i++;
      continue;
    }
    const level = +m[1];
    if (level <= parentLevel) {
      // Belongs to an outer section; stop here so the outer call can
      // pick it up.
      break;
    }
    const id = node.getAttribute('id') || '';
    const section = [];
    let j = i + 1;
    while (j < sourceNodes.length) {
      const sib = sourceNodes[j];
      const sibTag = sib.nodeType === 1 ? sib.tagName : '';
      const sm = HEADING_RE.exec(sibTag || '');
      if (sm && +sm[1] <= level) break;
      section.push(sib);
      j++;
    }
    const wrap = doc.createElement('div');
    wrap.setAttribute('class', 'heading-block');
    wrap.setAttribute('data-heading-id', id);
    wrap.setAttribute('data-heading-level', String(level));
    const headingClone = node.cloneNode(true);
    // Inject fold-toggle as first child of the heading so it appears
    // before the heading text (matches the prior runtime injection).
    const toggleHolder = doc.createElement('span');
    toggleHolder.innerHTML = FOLD_TOGGLE_HTML;
    const toggle = toggleHolder.firstChild;
    if (toggle) headingClone.insertBefore(toggle, headingClone.firstChild);
    wrap.appendChild(headingClone);
    const sect = doc.createElement('div');
    sect.setAttribute('class', 'heading-section');
    // Recurse so any deeper-level headings inside this section are
    // also wrapped.
    const innerNodes = wrapNodeListForFold(doc, section, level);
    for (const n of innerNodes) sect.appendChild(n);
    wrap.appendChild(sect);
    out.push(wrap);
    i = j;
  }
  return out;
}

export function wrapHeadingsForFold(html) {
  if (!/\<h[1-6]/i.test(html)) return html;

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;
  const children = Array.from(body.childNodes);

  const wrapped = wrapNodeListForFold(doc, children, 0);
  const out = doc.createElement('div');
  for (const n of wrapped) out.appendChild(n);
  return out.innerHTML;
}

const LANG_CLASS_RE = /\blanguage-([\w+\-#.]+)\b/;

export function wrapCodeBlocksForCopy(html) {
  if (!html.includes('<pre')) return html;

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;

  // Idempotency: if every <pre> is already inside a .code-block,
  // return the input unchanged.
  const pres = Array.from(body.querySelectorAll('pre'));
  if (pres.length === 0) return html;
  const allWrapped = pres.every((p) => p.parentElement && p.parentElement.classList.contains('code-block'));
  if (allWrapped) return html;

  for (const pre of pres) {
    if (pre.parentElement && pre.parentElement.classList.contains('code-block')) continue;

    let lang = pre.getAttribute('data-language') || '';
    if (!lang) {
      const code = pre.querySelector('code');
      if (code) {
        const cls = code.getAttribute('class') || '';
        const m = LANG_CLASS_RE.exec(cls);
        if (m) lang = m[1];
      }
    }
    if (!lang) lang = 'text';

    const wrap = doc.createElement('div');
    wrap.setAttribute('class', 'code-block');
    const meta = doc.createElement('div');
    meta.setAttribute('class', 'code-meta');
    const langSpan = doc.createElement('span');
    langSpan.setAttribute('class', 'code-lang');
    langSpan.textContent = lang;
    const btn = doc.createElement('button');
    btn.setAttribute('class', 'code-copy-btn');
    btn.setAttribute('type', 'button');
    btn.setAttribute('aria-label', 'Copy code');
    const lbl = doc.createElement('span');
    lbl.setAttribute('class', 'code-copy-label');
    lbl.textContent = 'Copy';
    btn.appendChild(lbl);
    meta.appendChild(langSpan);
    meta.appendChild(btn);

    pre.parentNode.insertBefore(wrap, pre);
    wrap.appendChild(meta);
    wrap.appendChild(pre);
  }

  return body.innerHTML;
}
